import { useDisclosure } from "@chakra-ui/hooks";
import { Modal, ModalBody, ModalHeader, ModalFooter, ModalOverlay } from "@chakra-ui/modal";
import { createWhiteListClient, getWhitwListRecordById, updateWhiteListRecord } from "api/whiteListed";
import Card from "components/card/custom";
import Checkbox from "components/checkbox";
import DivLoader from "components/divloader/DivLoader";
import InputField from "components/fields/InputField";
import React from "react";
import { toast } from "react-hot-toast";
import { MdHighlightOff } from "react-icons/md";

export interface iCreateOrgForm {
  first_name: string;
  last_name: string;
  bin: string;
  last_four: string;
  email: string;
  is_binAndlast_four?: any;
  is_client_name?: any;
  is_email?: any;
}

const CreateBlockClientsModal = ({
  fetchBlackListClientsData,
  id,
  info,
  roleData,
  infoData,
  client_id,
  gateway_id,
}: {
  fetchBlackListClientsData: () => void;
  id?: string;
  info?: boolean;
  roleData?: any;
  infoData?: any;
  client_id?: any;
  gateway_id?: any;
}) => {
  const rootForm = {
    first_name: "", last_name: "", bin: "", last_four: "", email: "",
    is_binAndlast_four: false, is_client_name: false, is_email: false,
  };

  const { isOpen, onOpen, onClose } = useDisclosure();
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [formValues, setFormValues] = React.useState<any>(rootForm);
  const [viewMode, setViewMode] = React.useState<boolean>(info);
  const [formValuesErr, setFormValuesErr] = React.useState<any>(rootForm);
  const [isCloseModal, setIsCloseModal] = React.useState<boolean>(false);
  const [isDataLoading, setIsDataLoading] = React.useState<boolean>(false);

  const handleClose = () => {
    setFormValues(rootForm);
    setFormValuesErr(rootForm);
    setIsCloseModal(true);
    setTimeout(() => { setIsCloseModal(false); onClose(); }, 300);
  };

  function handleValueChange(e: any) {
    const { id: fieldId, value } = e.target;
    setFormValues((p: any) => ({ ...p, [fieldId]: value }));
    setFormValuesErr((p: any) => ({ ...p, [fieldId]: "" }));
  }

  function validateData() {
    let isValid = false;
    let ok_last_four = true, ok_bin = true, ok_name = true, ok_email = true;

    if (formValues?.is_binAndlast_four) {
      isValid = true;
      if (formValues?.bin?.length !== 6) {
        setFormValuesErr((p: any) => ({ ...p, bin: "Please enter a 6-digit BIN number" }));
        ok_bin = false;
      }
    }
    if (formValues?.is_client_name) {
      isValid = true;
      if (!formValues?.first_name) {
        setFormValuesErr((p: any) => ({ ...p, first_name: "Please enter first name" }));
        ok_name = false;
      }
    }
    if (formValues?.is_email) {
      isValid = true;
      const regex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      if (!regex.test(formValues?.email)) {
        setFormValuesErr((p: any) => ({ ...p, email: "Please enter a valid email address" }));
        ok_email = false;
      }
    }

    if (ok_last_four && ok_bin && ok_name && ok_email && isValid) return true;
    if (!formValues?.is_email && !formValues?.is_binAndlast_four) {
      toast.error("Please provide either card details or an email address.");
    }
    return false;
  }

  async function handleSubmit(e: any) {
    e.preventDefault();
    if (!validateData()) return;
    try {
      setIsLoading(true);
      const base = {
        bin: formValues?.is_binAndlast_four ? formValues?.bin : "",
        last_four: formValues?.is_binAndlast_four ? formValues?.last_four : "",
        first_name: formValues?.is_client_name ? formValues?.first_name : "",
        last_name: formValues?.is_client_name ? formValues?.last_name : "",
        email: formValues?.is_email ? formValues?.email : "",
        client_id,
        gateway_id,
      };
      if (id) { await updateWhiteListRecord({ id, ...base }); }
      else { await createWhiteListClient(base); toast.success("Record added successfully!"); }
      fetchBlackListClientsData();
      handleClose();
    } catch (err: any) {
      toast.error(err?.response?.status === 422 ? "Please provide valid values" : (err?.response?.data?.message ?? "Unable to save record"));
    } finally {
      setIsLoading(false);
    }
  }

  async function fetchBlockedUserById(recordId: string) {
    setIsDataLoading(true);
    await getWhitwListRecordById(recordId)
      .then((data: any) => {
        setFormValues({
          first_name: data[0]?.first_name,
          last_name: data[0]?.last_name,
          bin: data[0]?.bin,
          last_four: data[0]?.last_four,
          email: data[0]?.email,
          is_binAndlast_four: !!data[0]?.bin,
          is_client_name: !!data[0]?.first_name,
          is_email: !!data[0]?.email,
        });
      })
      .catch((err: any) => toast.error(err?.response?.data?.message ?? "Unable to fetch data"))
      .finally(() => setIsDataLoading(false));
  }

  const openModal = () => {
    if (id) fetchBlockedUserById(id);
    onOpen();
  };

  const triggerButton = info ? (
    roleData?.[0]?.whitelist?.value?.view_whitelist && (
      <button className="wm-icon-btn wm-icon-btn--info" onClick={openModal} title="View record">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
        </svg>
      </button>
    )
  ) : id ? (
    <button className="wm-icon-btn wm-icon-btn--edit" onClick={openModal} title="Edit record">
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
      </svg>
    </button>
  ) : roleData?.[0]?.whitelist?.value?.add_whitelist ? (
    <button className="wm-add-btn" onClick={openModal}>
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="16"/><line x1="8" y1="12" x2="16" y2="12"/>
      </svg>
      Add Whitelist Entry
    </button>
  ) : null;

  const panelTitle = viewMode
    ? "View Whitelist Record"
    : id ? "Edit Whitelist Record"
    : "Add Whitelist Entry";

  return (
    <>
      {triggerButton}

      <Modal isOpen={isOpen} onClose={() => {}}>
        <ModalOverlay style={{ backdropFilter : "blur(4px)", background: "rgba(15,23,42,.55)" }} />
        <div className={`wm-panel${isOpen && !isCloseModal ? " wm-panel--open" : " wm-panel--closed"}`}>
          <div className="wz-top-glow" />
          {/* <div className="wm-panel-header">
            <button className="wm-back-btn" onClick={handleClose}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M19 12H5M12 19l-7-7 7-7"/>
              </svg>
            </button>
            <div>
              <h2 className="wm-panel-title"></h2>
              {id && <p className="wm-panel-sub">Record ID: {id}</p>}
            </div>
          </div> */}
          <div className="wz-header">
            <div className="wz-header-brand">
              <div className="wz-header-logo">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  {viewMode ? <><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><circle cx="12" cy="16" r="0.5" fill="#fff"/></> : id ? <><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></> : <><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="16"/><line x1="8" y1="12" x2="16" y2="12"/></>}
                </svg>
              </div>
              <div>
                <h2 className="wz-header-title">
                  {panelTitle}
                </h2>
              </div>
            </div>
            <div className="wz-header-right">
              <button className="wz-close-btn" onClick={handleClose} aria-label="Close">
                <MdHighlightOff size={22} />
              </button>
            </div>
          </div>

          <div className="wm-panel-body">
            {isDataLoading ? (
              <div className="wm-loading">
                <div className="wm-spinner" />
                <p>Loading record…</p>
              </div>
            ) : (
              <>
                <div className="wm-notice">
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                  </svg>
                  <span>You must provide either card details or an email address.</span>
                </div>

                {/* Card Details Section */}
                <div className="wm-section">
                  <div className={formValues?.is_binAndlast_four ? 'wm-section-header' : 'wm-section-header-closed'}>
                    <label className="wm-check-row">
                      <div className="wm-custom-check">
                        <input
                          type="checkbox"
                          id="is_binAndlast_four"
                          checked={formValues?.is_binAndlast_four}
                          disabled={viewMode}
                          onChange={(e) => setFormValues((p: any) => ({ ...p, is_binAndlast_four: e.target.checked }))}
                        />
                        <div className="wm-check-box" />
                      </div>
                      <div>
                        <span className="wm-check-label">Card Details</span>
                        <span className="wm-check-desc">Include BIN and last 4 digits</span>
                      </div>
                    </label>
                  </div>

                  {formValues?.is_binAndlast_four && (
                    <div className="wm-fields-row">
                      <div className="wm-field-wrap">
                        <label className="wm-field-label">BIN Number <span className="wm-required">*</span></label>
                        <input
                          className={`wm-input${formValuesErr?.bin ? " wm-input--error" : ""}`}
                          placeholder="e.g. 546470"
                          id="bin"
                          type="number"
                          disabled={viewMode}
                          value={formValues?.bin}
                          onChange={(e) => {
                            if (e.target.value.length <= 6) {
                              setFormValues((p: any) => ({ ...p, bin: e.target.value }));
                              setFormValuesErr((p: any) => ({ ...p, bin: "" }));
                            }
                          }}
                        />
                        {formValuesErr?.bin && <p className="wm-field-err">{formValuesErr.bin}</p>}
                      </div>
                      <div className="wm-field-wrap">
                        <label className="wm-field-label">Last 4 Digits</label>
                        <input
                          className={`wm-input${formValuesErr?.last_four ? " wm-input--error" : ""}`}
                          placeholder="e.g. 9876"
                          id="last_four"
                          type="number"
                          disabled={viewMode}
                          value={formValues?.last_four}
                          onChange={(e) => {
                            const v = e.target.value;
                            if (/^\d{0,4}$/.test(v)) {
                              setFormValues((p: any) => ({ ...p, last_four: v }));
                              setFormValuesErr((p: any) => ({ ...p, last_four: v.length === 4 ? "" : "Must be exactly 4 digits" }));
                            }
                          }}
                        />
                        {formValuesErr?.last_four && <p className="wm-field-err">{formValuesErr.last_four}</p>}
                      </div>
                    </div>
                  )}
                </div>

                {/* Name Section */}
                <div className="wm-section">
                  <div className={formValues?.is_client_name ? 'wm-section-header' : 'wm-section-header-closed'}>
                    <label className="wm-check-row">
                      <div className="wm-custom-check">
                        <input
                          type="checkbox"
                          id="is_client_name"
                          checked={formValues?.is_client_name}
                          disabled={viewMode}
                          onChange={(e) => setFormValues((p: any) => ({ ...p, is_client_name: e.target.checked }))}
                        />
                        <div className="wm-check-box" />
                      </div>
                      <div>
                        <span className="wm-check-label">Client Name</span>
                        <span className="wm-check-desc">Include first and last name</span>
                      </div>
                    </label>
                  </div>

                  {formValues?.is_client_name && (
                    <div className="wm-fields-row">
                      <div className="wm-field-wrap">
                        <label className="wm-field-label">First Name</label>
                        <input
                          className={`wm-input${formValuesErr?.first_name ? " wm-input--error" : ""}`}
                          placeholder="Enter first name"
                          id="first_name"
                          type="text"
                          disabled={viewMode}
                          value={formValues?.first_name}
                          onChange={handleValueChange}
                        />
                        {formValuesErr?.first_name && <p className="wm-field-err">{formValuesErr.first_name}</p>}
                      </div>
                      <div className="wm-field-wrap">
                        <label className="wm-field-label">Last Name</label>
                        <input
                          className="wm-input"
                          placeholder="Enter last name"
                          id="last_name"
                          type="text"
                          disabled={viewMode}
                          value={formValues?.last_name}
                          onChange={handleValueChange}
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* Email Section */}
                <div className="wm-section">
                  <div className={formValues?.is_email ? 'wm-section-header' : 'wm-section-header-closed'}>
                    <label className="wm-check-row">
                      <div className="wm-custom-check">
                        <input
                          type="checkbox"
                          id="is_email"
                          checked={formValues?.is_email}
                          disabled={viewMode}
                          onChange={(e) => setFormValues((p: any) => ({ ...p, is_email: e.target.checked }))}
                        />
                        <div className="wm-check-box" />
                      </div>
                      <div>
                        <span className="wm-check-label">Email Address <span className="wm-required">*</span></span>
                        <span className="wm-check-desc">Include a contact email</span>
                      </div>
                    </label>
                  </div>

                  {formValues?.is_email && (
                    <div className="wm-field-wrap">
                      <label className="wm-field-label">Email</label>
                      <input
                        className={`wm-input${formValuesErr?.email ? " wm-input--error" : ""}`}
                        placeholder="example@company.com"
                        id="email"
                        type="email"
                        disabled={viewMode}
                        value={formValues?.email}
                        onChange={handleValueChange}
                      />
                      {formValuesErr?.email && <p className="wm-field-err">{formValuesErr.email}</p>}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>

          <div className="wm-panel-footer">
            <button className="wm-btn wm-btn--ghost" onClick={handleClose}>Cancel</button>
            {!viewMode && (
              <button className="wm-btn wm-btn--primary" onClick={handleSubmit} disabled={isLoading}>
                {isLoading ? <span className="wm-btn-spinner" /> : null}
                {id ? "Update Record" : "Add Record"}
              </button>
            )}
          </div>
        </div>
      </Modal>

      <WmStyles />
    </>
  );
};

export default CreateBlockClientsModal;

const WmStyles = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');
    .wz-top-glow { height:3px;background:linear-gradient(90deg,#0f2744,#2563eb,#7c3aed,#2563eb,#0f2744);background-size:200% 100%;animation:glowMove 3s linear infinite;flex-shrink:0; }
    @keyframes glowMove { 0%{background-position:0% 0%} 100%{background-position:200% 0%} }
    .wz-header { padding:15px 26px;background:linear-gradient(135deg,#0f2744 0%,#1a3d6b 60%,#1d4ed8 100%);display:flex;align-items:center;justify-content:space-between;flex-shrink:0; }
    .wz-header-brand { display:flex;align-items:center;gap:13px; }
    .wz-header-logo { width:42px;height:42px;border-radius:12px;background:rgba(255,255,255,.15);border:1.5px solid rgba(255,255,255,.25);display:flex;align-items:center;justify-content:center;flex-shrink:0; }
    .wz-header-title { font-size:16px;font-weight:800;color:#fff;letter-spacing:-.3px;margin-bottom:2px;line-height:1; }
    .wz-header-sub { font-size:12px;color:rgba(255,255,255,.55);font-weight:500; }
    .wz-header-right { display:flex;align-items:center;gap:10px; }
    .wz-edit-btn { display:inline-flex;align-items:center;gap:5px;font-family:'Plus Jakarta Sans',sans-serif;font-size:12px;font-weight:700;color:#fff;background:rgba(255,255,255,.14);border:1.5px solid rgba(255,255,255,.25);border-radius:8px;padding:7px 12px;cursor:pointer;transition:background .15s; }
    .wz-edit-btn:hover { background:rgba(255,255,255,.24); }
    .wz-close-btn { background:none;border:none;cursor:pointer;color:rgba(255,255,255,.65);display:flex;align-items:center;padding:2px;transition:color .15s; }
    .wz-close-btn:hover { color:#fff; }
    .wm-panel { font-family: 'Plus Jakarta Sans', sans-serif; position: fixed; top: 0; right: 0; height: 100vh; width: 100%; max-width: 600px; background: #ffffff; z-index: 1004; display: flex; flex-direction: column; box-shadow: -8px 0 40px rgba(15,23,42,.12); transition: transform .3s cubic-bezier(.4,0,.2,1); }
    .wm-panel--open  { transform: translateX(0); }
    .wm-panel--closed { transform: translateX(100%); }

    .wm-panel-header { display: flex; align-items: center; gap: 16px; padding: 24px 28px; background: linear-gradient(135deg, #1d4ed8, #2563eb); flex-shrink: 0; }
    .wm-back-btn { width: 36px; height: 36px; border-radius: 10px; background: rgba(255,255,255,.18); border: 1px solid rgba(255,255,255,.25); display: flex; align-items: center; justify-content: center; color: #fff; cursor: pointer; transition: background .15s; flex-shrink: 0; }
    .wm-back-btn:hover { background: rgba(255,255,255,.28); }
    .wm-panel-title { font-size: 17px; font-weight: 700; color: #fff; letter-spacing: -.3px; }
    .wm-panel-sub { font-size: 12px; color: rgba(255,255,255,.7); font-weight: 500; margin-top: 2px; }

    .wm-panel-body { flex: 1; overflow-y: auto; padding: 24px 28px; display: flex; flex-direction: column; gap: 16px; }
    .wm-panel-body::-webkit-scrollbar { width: 4px; }
    .wm-panel-body::-webkit-scrollbar-thumb { background: #e2e8f0; border-radius: 100px; }
    .wm-panel-footer { padding: 20px 28px; border-top: 1px solid #e2e8f0; display: flex; align-items: center; justify-content: flex-end; gap: 10px; flex-shrink: 0; background: #fafbfc; }

    .wm-loading { display: flex; align-items: center; justify-content: center; gap: 12px; padding: 60px 20px; }
    .wm-spinner { width: 24px; height: 24px; border: 3px solid #e2e8f0; border-top-color: #2563eb; border-radius: 50%; animation: wmSpin .7s linear infinite; }
    @keyframes wmSpin { to { transform: rotate(360deg); } }

    .wm-notice { display: flex; align-items: center; gap: 10px; padding: 12px 16px; background: #eff6ff; border: 1.5px solid #bfdbfe; border-radius: 10px; font-size: 13px; font-weight: 600; color: #1d4ed8; }

    .wm-section { background: #ffffff; border: 1.5px solid #e2e8f0; border-radius: 14px; }
    .wm-section-header { padding: 16px 20px; background: #fafbfc; border-bottom: 1px solid #f1f5f9;border-radius: 14px 14px 0px 0px; }
    .wm-section-header-closed { padding: 16px 20px; background: #fafbfc; border-bottom: 1px solid #f1f5f9;border-radius: 14px 14px 14px 14px; }
    .wm-section .wm-field-wrap, .wm-section .wm-fields-row { padding: 16px 20px; border-top: 1px solid #f1f5f9; }

    .wm-check-row { display: flex; align-items: flex-start; gap: 12px; cursor: pointer; }
    .wm-custom-check { position: relative; flex-shrink: 0; margin-top: 2px; }
    .wm-custom-check input[type="checkbox"] { position: absolute; opacity: 0; width: 18px; height: 18px; cursor: pointer; z-index: 1; }
    .wm-check-box { width: 18px; height: 18px; border-radius: 5px; border: 2px solid #cbd5e1; background: #fff; transition: all .15s; }
    .wm-custom-check input:checked ~ .wm-check-box { background: #2563eb; border-color: #2563eb; background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 12 10' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 5l3.5 3.5L11 1' stroke='white' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E"); background-repeat: no-repeat; background-position: center; background-size: 10px; }
    .wm-custom-check input:disabled ~ .wm-check-box { background: #f1f5f9; border-color: #e2e8f0; cursor: not-allowed; }
    .wm-check-label { display: block; font-size: 13.5px; font-weight: 700; color: #0f172a; }
    .wm-check-desc { display: block; font-size: 12px; color: #94a3b8; font-weight: 500; margin-top: 2px; }

    .wm-fields-row { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
    .wm-field-wrap { display: flex; flex-direction: column; gap: 6px; }
    .wm-field-label { font-size: 12px; font-weight: 700; color: #64748b; text-transform: uppercase; letter-spacing: .4px; }
    .wm-required { color: #ef4444; }
    .wm-input { width: 100%; height: 40px; padding: 0 12px; border: 1.5px solid #e2e8f0; border-radius: 10px; font-family: 'Plus Jakarta Sans', sans-serif; font-size: 13.5px; font-weight: 600; color: #0f172a; background: #fff; outline: none; transition: border-color .15s, box-shadow .15s; box-sizing: border-box; }
    .wm-input:focus { border-color: #2563eb; box-shadow: 0 0 0 3px rgba(37,99,235,.1); }
    .wm-input:disabled { background: #f8fafc; color: #94a3b8; cursor: not-allowed; }
    .wm-input--error { border-color: #ef4444; }
    .wm-input--error:focus { border-color: #ef4444; box-shadow: 0 0 0 3px rgba(239,68,68,.1); }
    .wm-field-err { font-size: 12px; font-weight: 600; color: #ef4444; }

    .wm-btn { display: inline-flex; align-items: center; gap: 8px; padding: 10px 20px; border-radius: 10px; font-family: 'Plus Jakarta Sans', sans-serif; font-size: 13.5px; font-weight: 700; cursor: pointer; border: none; transition: all .15s; }
    .wm-btn--ghost { background: #f1f5f9; color: #64748b; border: 1.5px solid #e2e8f0; }
    .wm-btn--ghost:hover { background: #e2e8f0; }
    .wm-btn--primary { background: linear-gradient(135deg,#1d4ed8,#2563eb); color: #fff; box-shadow: 0 2px 8px rgba(37,99,235,.3); }
    .wm-btn--primary:hover:not(:disabled) { background: linear-gradient(135deg,#1e40af,#1d4ed8); box-shadow: 0 4px 16px rgba(37,99,235,.4); }
    .wm-btn--primary:disabled { opacity: .65; cursor: not-allowed; }
    .wm-btn-spinner { width: 14px; height: 14px; border: 2px solid rgba(255,255,255,.4); border-top-color: #fff; border-radius: 50%; animation: wmSpin .7s linear infinite; }

    .wm-icon-btn { width: 32px; height: 32px; border-radius: 8px; border: 1.5px solid #e2e8f0; background: #fff; display: inline-flex; align-items: center; justify-content: center; cursor: pointer; transition: all .15s; }
    .wm-icon-btn--info { color: #2563eb; }
    .wm-icon-btn--info:hover { background: #eff6ff; border-color: #bfdbfe; }
    .wm-icon-btn--edit { color: #2563eb; }
    .wm-icon-btn--edit:hover { background: #eff6ff; border-color: #bfdbfe; }

    .wm-add-btn { display: inline-flex; align-items: center; gap: 8px; padding: 10px 18px; border-radius: 10px; font-family: 'Plus Jakarta Sans', sans-serif; font-size: 13px; font-weight: 700; color: #fff; background: linear-gradient(135deg,#1d4ed8,#2563eb); border: none; cursor: pointer; transition: all .15s; box-shadow: 0 2px 8px rgba(37,99,235,.25); }
    .wm-add-btn:hover { background: linear-gradient(135deg,#1e40af,#1d4ed8); box-shadow: 0 4px 14px rgba(37,99,235,.35); }
  `}</style>
);